import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Chip, CircularProgress, Grid, TextField, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import FilterListIcon from '@material-ui/icons/FilterList';
import GroupAddOutlinedIcon from '@material-ui/icons/GroupAddOutlined';
import { ATTRIBUTE_FILTERS, hasActiveFilters } from './assignmentFilters';

const useStyles = makeStyles(theme => ({
    root: {
        marginBottom: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 12,
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
    },
    header: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) },
    title: { fontSize: '0.875rem', fontWeight: 700, color: 'var(--dm-text-strong, #263548)' },
    hint: { fontSize: '0.75rem', color: 'var(--dm-muted, #647184)' },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.25),
    },
    count: { fontSize: '0.8125rem', fontWeight: 600, color: 'var(--dm-text-strong, #263548)' },
    actions: { display: 'flex', gap: theme.spacing(1) },
}));

const optionLabel = option => (option.count === null || option.count === undefined ? option.value : `${option.value} (${option.count})`);

const AttributeFilterBar = ({ facets, filters, onChange, matchCount, totalUsers, matching, onAssignMatching, disabled }) => {
    const classes = useStyles();
    const available = ATTRIBUTE_FILTERS.filter(filter => facets[filter.key]?.length);
    if (!available.length) return null;
    const active = hasActiveFilters(filters);

    const setValues = (key, values) => onChange({ ...filters, [key]: values });

    return (
        <section className={classes.root} aria-labelledby="attribute-filter-title">
            <div className={classes.header}>
                <FilterListIcon fontSize="small" color="primary" aria-hidden="true" />
                <Typography id="attribute-filter-title" className={classes.title}>Filter users by profile</Typography>
                <Typography className={classes.hint}>Pick values to narrow the matrix, then assign licences to everyone who matches.</Typography>
            </div>
            <Grid container spacing={1}>
                {available.map(filter => (
                    <Grid item xs={12} sm={6} md={3} key={filter.key}>
                        <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            options={facets[filter.key]}
                            value={facets[filter.key].filter(option => (filters[filter.key] || []).includes(option.value))}
                            getOptionLabel={optionLabel}
                            getOptionSelected={(option, selected) => option.value === selected.value}
                            onChange={(event, values) => setValues(filter.key, values.map(option => option.value))}
                            renderOption={(option, { selected }) => (
                                <>
                                    <Checkbox color="primary" size="small" checked={selected} style={{ marginRight: 6 }} />
                                    {optionLabel(option)}
                                </>
                            )}
                            renderTags={(values, getTagProps) => values.map((option, index) => (
                                <Chip size="small" label={option.value} {...getTagProps({ index })} />
                            ))}
                            renderInput={params => <TextField {...params} variant="outlined" label={filter.label} />}
                        />
                    </Grid>
                ))}
            </Grid>
            {active ? (
                <div className={classes.footer}>
                    <Typography className={classes.count} role="status">
                        {matching ? 'Finding matching users…' : `${matchCount} of ${totalUsers} users match`}
                    </Typography>
                    <div className={classes.actions}>
                        <Button size="small" onClick={() => onChange({})}>Clear filters</Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={matching ? <CircularProgress size={14} color="inherit" /> : <GroupAddOutlinedIcon fontSize="small" />}
                            disabled={disabled || matching || !matchCount}
                            onClick={onAssignMatching}
                        >
                            Assign to all matching
                        </Button>
                    </div>
                </div>
            ) : null}
        </section>
    );
};

AttributeFilterBar.propTypes = {
    facets: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    matchCount: PropTypes.number,
    totalUsers: PropTypes.number,
    matching: PropTypes.bool,
    onAssignMatching: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default AttributeFilterBar;
